import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';

/**
 * Reusable multi-select dropdown component
 */
const MultiSelectDropdown = ({
  label,
  value = [],
  options = [],
  onChange,
  placeholder = 'Select options...',
  emptyText = 'No options available',
  clearText = 'Clear all',
  className = '',
  disabled = false,
  maxHeight = '16rem'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleOption = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const getDisplayText = () => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) {
      const option = options.find(opt => opt.value === value[0]);
      return option?.label || value[0];
    }
    return `${value.length} selected`;
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="text-xs font-medium text-base-content/60 mb-2 block">
          {label}
        </label>
      )}

      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2 bg-base-100 border border-base-300 rounded-lg flex items-center justify-between transition-colors ${
          disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:border-base-400 focus:border-primary focus:outline-none'
        }`}
      >
        <span className="text-sm truncate">
          {getDisplayText()}
        </span>
        <ChevronDownIcon
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {isOpen && !disabled && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="absolute z-10 mt-1 w-full bg-white border border-base-200 rounded-lg shadow-lg overflow-hidden"
          >
            <div
              className="overflow-y-auto"
              style={{ maxHeight }}
            >
              {options.length === 0 ? (
                <div className="px-3 py-4 text-center text-sm text-base-content/50">
                  {emptyText}
                </div>
              ) : (
                options.map(option => (
                  <label
                    key={option.value}
                    className="flex items-center px-3 py-2 hover:bg-base-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={value.includes(option.value)}
                      onChange={() => handleToggleOption(option.value)}
                      className="checkbox checkbox-sm"
                    />
                    <span className="ml-3 text-sm flex-1">
                      {option.label}
                    </span>
                    {value.includes(option.value) && (
                      <CheckIcon className="w-4 h-4 text-primary" />
                    )}
                  </label>
                ))
              )}
            </div>

            {value.length > 0 && (
              <div className="border-t border-base-200 p-2">
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="w-full text-xs text-base-content/60 hover:text-base-content transition-colors"
                >
                  {clearText}
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MultiSelectDropdown;